import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    List,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';

import AdminNodeStore from '../../../stores/admin-node-store';
import UserService from '../../../http/user-service';
import { NodeService } from '../../../http/node-service';
import { DbNode } from '../../../types/node';
import { DbUser, UserRole } from '../../../types/user';
import NodePickerItem from '../NodePickerItem';

//=================================================================================================
interface EditUserDialogProps {
    user: DbUser;
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    rootNodeId: number;
}

const ROLE_LABELS: Record<UserRole, string> = {
    [UserRole.Administrator]: 'Администратор',
    [UserRole.User]: 'Пользователь',
};

const EditUserDialog = observer(({ user, open, onClose, onSaved, rootNodeId }: EditUserDialogProps) => {
    const [rootStore] = useState(() => new AdminNodeStore(rootNodeId));

    const [type, setType] = useState<UserRole>(UserRole.User);
    const [email, setEmail] = useState('');
    const [description, setDescription] = useState('');
    const [selectedNode, setSelectedNode] = useState<DbNode | null>(null);
    const [nodeLoading, setNodeLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setType(user.type);
        setEmail(user.email ?? '');
        setDescription(user.description ?? '');
        setError('');
        setSelectedNode(null);
        setNodeLoading(true);
        rootStore.loadNode().catch(() => {});
        NodeService.getById(user.node_id)
            .then((res) => setSelectedNode(res.data))
            .catch(() => {})
            .finally(() => setNodeLoading(false));
    }, [open, user]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        if (loading) return;
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        try {
            await UserService.edit({
                ...user,
                type,
                node_id: selectedNode?.node_id ?? user.node_id,
                email: email.trim() || null,
                description: description.trim() || null,
            });
            onSaved();
            handleClose();
        } catch {
            setError('Не удалось сохранить');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !loading) handleSubmit(); }}
        >
            <DialogTitle>Редактировать — {user.login}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                <FormControl fullWidth size="small">
                    <InputLabel>Роль</InputLabel>
                    <Select label="Роль" value={type} onChange={(e) => setType(e.target.value as UserRole)} autoFocus>
                        {(Object.values(UserRole).filter((v) => typeof v === 'number') as UserRole[]).map((r) => (
                            <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Email (необязательно)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    size="small"
                />
                <TextField
                    label="Описание (необязательно)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                />
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Нода</Typography>
                        {nodeLoading ? (
                            <CircularProgress size={12} />
                        ) : selectedNode ? (
                            <Chip label={selectedNode.name} size="small" onDelete={() => setSelectedNode(null)} />
                        ) : null}
                    </Box>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 200, overflowY: 'auto' }}>
                        {rootStore.loading && rootStore.items.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <List dense disablePadding sx={{ p: 0.5 }}>
                                {rootStore.items.map((node) => (
                                    <NodePickerItem
                                        key={node.node_id}
                                        node={node}
                                        depth={0}
                                        selectedNodeId={selectedNode?.node_id ?? null}
                                        onSelect={setSelectedNode}
                                    />
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Отмена</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading} disableElevation>
                    {loading ? <CircularProgress size={18} /> : 'Сохранить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default EditUserDialog;
